import React, { useEffect, useState } from "react";
import api from "../api";
import { PageHeader, EmptyState, inr } from "../components/UI";
import { TrendingUp, DollarSign, AlertCircle, Lock } from "lucide-react";

export default function ProfitReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/profit-report")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-[#475569]">Loading profit insights…</div>;
  if (!data) return <div className="p-8 text-red-600">Failed to load.</div>;

  const t = data.totals;
  return (
    <div className="p-4 lg:p-8 max-w-7xl" data-testid="profit-page">
      <PageHeader
        title="Profit Report"
        subtitle="Per-product profit margins · Admin-only view"
      />

      <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900 flex items-center gap-2">
        <Lock size={14}/> This page is visible only to admins. Cost prices are sensitive — only set them on the Products page.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 stagger">
        <div className="card-flat p-5" data-testid="kpi-revenue">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Total Revenue</div>
          <div className="font-display font-bold text-2xl font-mono-num mt-1">{inr(t.total_revenue)}</div>
        </div>
        <div className="card-flat p-5" data-testid="kpi-cost">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Total Cost</div>
          <div className="font-display font-bold text-2xl font-mono-num mt-1 text-[#475569]">{inr(t.total_cost)}</div>
        </div>
        <div className="card-flat p-5" data-testid="kpi-profit">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Total Profit</div>
          <div className="font-display font-bold text-2xl font-mono-num mt-1 text-green-700">{inr(t.total_profit)}</div>
        </div>
        <div className="card-flat p-5" data-testid="kpi-margin">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#475569]">Overall Margin</div>
          <div className="font-display font-bold text-2xl mt-1 text-[#C1121F]">{t.overall_margin_pct}%</div>
        </div>
      </div>

      {t.products_without_cost > 0 && (
        <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded text-xs flex items-center gap-2" data-testid="cost-warning">
          <AlertCircle className="text-amber-600 flex-shrink-0" size={14}/>
          <span><b>{t.products_without_cost} products</b> don&apos;t have a cost price set — their profit is currently the same as revenue. Visit <a href="/products" className="text-[#C1121F] font-semibold hover:underline">Products</a> to set cost prices for accurate margins.</span>
        </div>
      )}

      {data.items.length === 0 ? (
        <EmptyState icon={TrendingUp} title="No sales data yet" hint="Once you have orders, this report will show profit per product." />
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="crm-table" data-testid="profit-table">
            <thead>
              <tr>
                <th>Product</th>
                <th className="text-right">Units Sold</th>
                <th className="text-right">Avg Price</th>
                <th className="text-right">Cost / Unit</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Cost</th>
                <th className="text-right">Profit</th>
                <th className="text-right">Margin %</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.product_name} data-testid={`prof-row-${r.product_name}`}>
                  <td className="font-semibold">{r.product_name}</td>
                  <td className="text-right font-mono-num">{r.units_sold.toLocaleString("en-IN")}</td>
                  <td className="text-right font-mono-num">{inr(r.avg_price)}</td>
                  <td className="text-right font-mono-num">{r.has_cost ? inr(r.cost_price) : <span className="text-[#94A3B8]">—</span>}</td>
                  <td className="text-right font-mono-num">{inr(r.revenue)}</td>
                  <td className="text-right font-mono-num text-[#475569]">{inr(r.cost)}</td>
                  <td className={`text-right font-mono-num font-semibold ${r.profit > 0 ? "text-green-700" : "text-red-600"}`}>{inr(r.profit)}</td>
                  <td className="text-right">
                    {r.has_cost ? (
                      <span className={`font-mono-num font-semibold ${r.margin_pct > 30 ? "text-green-700" : r.margin_pct > 10 ? "text-amber-600" : "text-red-600"}`}>
                        {r.margin_pct}%
                      </span>
                    ) : <span className="text-[#94A3B8]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
